import { addDays, startOfDay, subDays } from 'date-fns';
import itiriri from 'itiriri';
import {
  HSDrug,
  WardDto,
  HSPackedPatientDay,
  PatientMovementWithData
} from 'server-openapi';
import {PersistentQueue, QueueEntry} from '../core/queue/PersistentQueue';
import {Entry, IStorage} from '../core/storage/Contract';
import { SyncStreamAPI } from './api';
import {IFacilityGroupSyncService } from './SyncCenter';
import { SyncUtils, ChangeNumberWithDateString, ChangeNumberWithDate } from "./utils/SyncUtils";
import { DateUtils } from '../core/utils/dateUtils';
import { Logger } from '../core/logger/logger';
import { v4 as uuidv4 } from 'uuid';
import { PatchUtils } from '../pages/ResidentDetails/components/patches/PatchUtils';
import {MemoryCache} from "../core/storage/MemoryCache";
import {mergePackedPatientDays} from "./utils/PackedPatientDayUtils";

const logger = new Logger('SyncPackedPatientDays');

export class SyncPackedPatientDays implements IFacilityGroupSyncService {

    // This is the date that we will sync to for the current operation of syncing down.
    // It will always be one day into the future.
    private syncToDate: Date;

  get name(): string {
    return 'SyncPackedPatientDays';
  }
  private async isStale(p: HSPackedPatientDay): Promise<boolean> {
    return (!!(p.packDate && p.packDate < DateUtils.fromDate(subDays(startOfDay(new Date()), 7))));
  }
  private async isFacilityGroup(p: HSPackedPatientDay, facilityGroupId: string): Promise<boolean> {
    const facility = await this.facilitiesStore.get(p.facilityId!.toString());
    return facility?.facilityGroupId?.toString() == facilityGroupId;
  }

  constructor(
    private api: SyncStreamAPI,
    private storage: MemoryCache<HSPackedPatientDay>,
    private facilitiesStore: IStorage<WardDto>,
    private patientMovementStore: IStorage<PatientMovementWithData>,
    private latestChangeNumbers: IStorage<number | undefined>,
    private latestChangeNumbersWithDate: IStorage<ChangeNumberWithDateString>
  ) {
      this.syncToDate = startOfDay(addDays(new Date(), 1));
  }

  async load(facilityGroupId: string): Promise<void> {
    await this.storage.reset(async (p) => {
          return await this.isFacilityGroup(p, facilityGroupId);
        });
  }

    //eslint-disable-next-line sonarjs/cognitive-complexity
    async syncDown(facilityGroupId?: string) {
        // TODO: storage locking so that we can be sure the UI
        // didn't accidentally change a resource in between the API
        // giving us fresh data and updating the storage backend.
        const facilitiesToSync = await SyncUtils.getFacilitiesForGroup(facilityGroupId, this.facilitiesStore);

        // Perform check to see if old style packed patient day data, last change number storage is still present,
        // meaning no migration has been done since upgrade. Will also give us a list of stored packed patient day data
        // that have a pack date for beyond just one day into the future. This will be able to be deleted as will no
        // longer require storing them beyond that.
        const futurePackedPatientDaysToDelete = await this.migrate(facilitiesToSync);

        let changeNumberWithDate = await SyncUtils.getChangeNumberWithDateForFacilities(
            facilitiesToSync.map((x) => x.hsId!),
            this.latestChangeNumbersWithDate,
        );

        let packedPatientDayData: HSPackedPatientDay[] = new Array<HSPackedPatientDay>();
        const lastSyncToDay = changeNumberWithDate?.syncToDate ? startOfDay(changeNumberWithDate.syncToDate) : undefined;
        const sevenDaysAgo = subDays(startOfDay(new Date()), 7);
        let lastChangeNumber = changeNumberWithDate?.changeNumber ?? 0;

        // See if need to catch up on sync data; if we are now on a new date so the current syncToDate is greater than
        // what was recorded as used from the last time we synced.
        if(lastSyncToDay && lastSyncToDay < this.syncToDate) {
            const __ret = await this.performCatchupSyncDown(lastSyncToDay,
                sevenDaysAgo,
                facilityGroupId,
                facilitiesToSync,
                changeNumberWithDate,
                packedPatientDayData,
                lastChangeNumber);

            changeNumberWithDate = __ret.changeNumberWithDate;
            lastChangeNumber = __ret.lastChangeNumber;
        }

        // Now if we are not catching up, or will have just caught up, then do the regular sync
        packedPatientDayData = await this.syncFacilityDown(
            facilityGroupId!, //non null assertion on facility group id
            facilitiesToSync.map((facility) => facility.hsId!),
            changeNumberWithDate.changeNumber,
            undefined,
            sevenDaysAgo,
            this.syncToDate,
        );

        // If got any packed patient days, save them to the storage backend as well as the current max change number.
        if(packedPatientDayData && packedPatientDayData.length > 0) {
            await this.storage.setMany(
                packedPatientDayData
                    .map((packedPatientDay) => ({
                        key: this.storage.get_key!(packedPatientDay),
                        value: packedPatientDay,
                    })),
            );

            // The last packed patient day item will have the highest change number as that is how they get ordered. So grab
            // that value and we'll use it to update the local storage
            const maxChangeNumberExtracted = packedPatientDayData[packedPatientDayData.length - 1].changeNumber;

            if (maxChangeNumberExtracted) {
                lastChangeNumber = maxChangeNumberExtracted;
            }

            await SyncUtils.setChangeNumberWithDateForFacilities(
                facilitiesToSync.map((x) => x.hsId!),
                this.latestChangeNumbersWithDate,
                this.syncToDate,
                lastChangeNumber
            );
        }

        // Finally, handle completion of migration from old style last change number storage to new style if necessary.
        // The futurePackedPatientDaysToDelete will only be defined when started the syn down process and determined a
        // migration hadn't been done previously since upgrading.
        if (futurePackedPatientDaysToDelete) {
            // Delete the old style last change number storage
            await SyncUtils.deleteChangeNumberForFacilities(
                facilitiesToSync.map((x) => x.hsId!),
                this.latestChangeNumbers
            );

            // Delete the determined packed patient days that have pack date for beyond the no longer required date of
            // 1 day onto the future
            await this.storage.deleteMany(futurePackedPatientDaysToDelete);
        }
    }

    private async performCatchupSyncDown(lastSyncToDay: Date,
        sevenDaysAgo: Date,
        facilityGroupId: string | undefined,
        facilitiesToSync: any[] | WardDto[],
        changeNumberWithDate: ChangeNumberWithDate,
        packedPatientDayData: HSPackedPatientDay[],
        lastChangeNumber: number) {
        // The max period we need to sync from is 7 days. So, if the last sync was more than 7 days ago, we will
        // just limit it to 7 days.
        const startPackDate = lastSyncToDay > sevenDaysAgo ? lastSyncToDay : sevenDaysAgo;
        const changeNumberToSync = 0;

        const currentPackedPatientDayData = await this.syncFacilityDown(
            facilityGroupId!, // non null assertion on facility group id
            facilitiesToSync.map((facility) => facility.hsId!),
            changeNumberToSync,
            changeNumberWithDate.changeNumber,
            startPackDate,
            this.syncToDate
        );

        for (const packedPatientDay of currentPackedPatientDayData) {
            packedPatientDayData.push(packedPatientDay); // Add to the list of those to save

            if (packedPatientDay.changeNumber) {
                lastChangeNumber = packedPatientDay.changeNumber;

                if (packedPatientDay.changeNumber >= changeNumberWithDate.changeNumber) {
                    break;
                }
            }
        }

        // Save the packed patient days to the storage backend, for those up to the current max change number.
        await this.storage.setMany(
            packedPatientDayData
                .map((packedPatientDay) => ({
                    key: this.storage.get_key!(packedPatientDay),
                    value: packedPatientDay
                }))
        );

        // Save the new change number and sync date
        await SyncUtils.setChangeNumberWithDateForFacilities(
            facilitiesToSync.map((x) => x.hsId!),
            this.latestChangeNumbersWithDate,
            this.syncToDate,
            lastChangeNumber
        );

        // Re-get the change number if had done catch up
        changeNumberWithDate = await SyncUtils.getChangeNumberWithDateForFacilities(
            facilitiesToSync.map((x) => x.hsId!),
            this.latestChangeNumbersWithDate
        );

        lastChangeNumber = changeNumberWithDate.changeNumber;
        return { changeNumberWithDate, lastChangeNumber };
    }

    private async syncFacilityDown(
    facilityGroupId: string,
    facilityIds: number[],
    sinceChangeNumber: number,
    maxChangeNumber: number | undefined,
    startPackDate: Date,
    endPackDate: Date,
  ): Promise<HSPackedPatientDay[]> {
    const pageSize = 300;
    const packedPatientDays = await this.api.packedPatientDays.packedPatientDayListPackedPatientDay(
      sinceChangeNumber,
      parseInt(facilityGroupId),
      pageSize,
      maxChangeNumber,
      startPackDate.toDateString(),
      endPackDate.toDateString()
    )

    // Get the list of patient movements.  Sort them by id desc so the most recent is first.  We only care about the most recent.
    const patientMovements = [...(await this.patientMovementStore.all()).values()].sort((a, b) => (b.hsId ?? -1) - (a.hsId ?? -1));
    const mergedPackedPatientDays: HSPackedPatientDay[] = [];
    for(const ppd of packedPatientDays.data) {
      // Patients can move in and then out of this facility.
      // So, get the list of all patient movements in or out (sorted by id desc to get the most recent).
      const pm = patientMovements.find(x => x.patientId === ppd.patientId && (x.movedTo?.facilityId === ppd.facilityId || x.movedFrom?.facilityId === ppd.facilityId));
      let merged: HSPackedPatientDay | undefined;

      if (pm && pm.movedTo === ppd.facilityId) {
        // We have a patient movement, and it was a move in to this facility.
        const pm_ppd = pm.packedPatientDays?.find(x => x.packDate === ppd.packDate);
        if (pm_ppd) {
          // We have found a matching PPD.
          // mergedPackedPatientDays will return undefined if nothing was merged.
          merged = mergePackedPatientDays(ppd, pm_ppd);
        }
      }
      if (merged) {
        mergedPackedPatientDays.push(merged);
      }
      else {
        mergedPackedPatientDays.push(ppd);
      }
    }


    if (packedPatientDays.data.length === pageSize) {
      return [
        ...mergedPackedPatientDays,
        ...(await this.syncFacilityDown(
          facilityGroupId,
          facilityIds,

          // Note - need to get the cn from the original, NOT the merged!
          SyncUtils.getLatestChangeNumber(packedPatientDays.data)!,
          maxChangeNumber,
          startPackDate,
          endPackDate,
        )),
      ];
    }
    return mergedPackedPatientDays;
  }


  async clear() {
    await this.storage.clear();
    await this.latestChangeNumbers.clear();
  }

  isAllowed(canUserAccessMedication: boolean): boolean {
    // Only if you can view a round.
    return canUserAccessMedication;
  }
  async archive(deletedPatientIds: string[]): Promise<void> {
    const keysToDelete: string[] = [];
    for (const [k, v] of (await this.storage.all()).entries()) {
      if (await this.isStale(v) || deletedPatientIds.some(x => x === (v.patientId?.toString() ?? "-1"))) {
        keysToDelete.push(k)
      }
    }
    await this.storage.deleteMany(keysToDelete);
  }
  setEncryptionVersion(version: number): void {
    this.storage.compressOnSave = (version > 1);
  }
  async rewrite(): Promise<void> {
    const entries: Entry<HSPackedPatientDay>[] = [...(await this.storage.all())].map((keyValueArray) => {
      return {
        key: keyValueArray[0],
        value: keyValueArray[1]
      };
    });

    return await this.storage.setMany(entries);
  }

  async migrate(facilitiesToSync: WardDto[]): Promise<string[] | undefined> {
      const keysToDelete: string[] = [];
      let packDataMaxChangeNumber = 0;
      let packDateStringForMaxChangeNumber: string | undefined;

      // Get the latest change number for the old implementation for the facilities to sync.
      const oldStyleChangeNumber = await SyncUtils.getChangeNumberForFacilities(
          facilitiesToSync.map((x) => x.hsId!),
          this.latestChangeNumbers,
      );

      // If there was no change number present in the old table then it would have assigned 0, which means we don't need
      // to migrate as we have already done so or don't need to do so as there's no previous data.
      if (oldStyleChangeNumber === 0) {
          return undefined;
      }

      // Start by getting all the entries that are after the syncToDate as will now only require to store entries up to
      // the syncToDate (i.e. one day into the future). These will be returned so they can be deleted at the end of the
      // sync down operation. Also, while iterating the entries, for those that won't be deleted, will track to find the
      // current max change number and its pack date.
      for (const [k, v] of (await this.storage.all()).entries()) {
          if (v.packDate && DateUtils.toOffsetlessDate(v.packDate) > this.syncToDate || await this.isStale(v)) {
              keysToDelete.push(k);
          }
          else if (v.changeNumber && v.changeNumber > packDataMaxChangeNumber){
              packDataMaxChangeNumber = v.changeNumber;
              packDateStringForMaxChangeNumber = v.packDate;
          }
      }

      // Determine which is the lesser change number and synToDate to use.
      let smallerChangeNumber = packDataMaxChangeNumber;

      if(oldStyleChangeNumber > 0 && oldStyleChangeNumber < packDataMaxChangeNumber){
          smallerChangeNumber = oldStyleChangeNumber;
      }

      let  smallerSyncToPackDate =  this.syncToDate;

      if(packDateStringForMaxChangeNumber){
          const packDateForMaxChangeNumber = DateUtils.toOffsetlessDate(packDateStringForMaxChangeNumber);

          if(packDateForMaxChangeNumber < this.syncToDate){
              smallerSyncToPackDate = packDateForMaxChangeNumber;
          }
      }

      await SyncUtils.setChangeNumberWithDateForFacilities(
          facilitiesToSync.map((x) => x.hsId!),
          this.latestChangeNumbersWithDate,
          smallerSyncToPackDate,
          smallerChangeNumber
      );

      return keysToDelete
  }
}

